import { ArchivedProjectActions } from "./ArchivedProjectActions";
import { usePrimaryEnvironmentId } from "../../environments/primary";
import { ArchiveIcon, ArchiveX, LoaderIcon } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import { type DesktopUpdateChannel, type ScopedThreadRef } from "@t3tools/contracts";
import { scopeThreadRef } from "@t3tools/client-runtime";
import { DEFAULT_CLIENT_SETTINGS } from "@t3tools/contracts/settings";
import { APP_BASE_NAME, APP_VERSION } from "../../branding";
import {
  canCheckForUpdate,
  getDesktopUpdateButtonTooltip,
  getDesktopUpdateInstallConfirmationMessage,
  isDesktopUpdateButtonDisabled,
  resolveDesktopUpdateButtonAction,
} from "../../components/desktopUpdate.logic";
import { isElectron } from "../../env";
import { useAppVersion } from "../../hooks/useAppVersion";
import { useTheme } from "../../hooks/useTheme";
import {
  DEFAULT_LOCALE,
  isLocale,
  LOCALE_LABELS,
  LOCALES,
  setLocale,
  type Locale,
} from "../../i18n";
import { useSettings, useUpdateSettings } from "../../hooks/useSettings";
import { useThreadActions } from "../../hooks/useThreadActions";
import {
  setDesktopUpdateStateQueryData,
  useDesktopUpdateState,
} from "../../lib/desktopUpdateReactQuery";
import { ensureLocalApi, readLocalApi } from "../../localApi";
import { useShallow } from "zustand/react/shallow";
import { selectProjectsAcrossEnvironments, useStore } from "../../store";
import { useArchivedThreadSnapshots } from "../../lib/archivedThreadsState";
import { formatRelativeTimeLabel } from "../../timestampFormat";
import { Button } from "../ui/button";
import { Select, SelectItem, SelectPopup, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";
import { stackedThreadToast, toastManager } from "../ui/toast";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { formatDiagnosticsDescription } from "./SettingsPanels.logic";
import {
  SettingResetButton,
  SettingsPageContainer,
  SettingsRow,
  SettingsSection,
} from "./settingsLayout";
import { ProjectFavicon } from "../ProjectFavicon";
import { useServerObservability } from "../../rpc/serverState";
import { Trans, useTranslation } from "react-i18next";

const THEME_OPTIONS = [
  {
    value: "system",
    label: "settings/system_ip84wd",
  },
  {
    value: "light",
    label: "settings/light_or1hvb",
  },
  {
    value: "dark",
    label: "settings/dark_qwkhsj",
  },
] as const;

const TIMESTAMP_FORMAT_LABELS = {
  locale: "settings/systemDefault_sqqtix",
  "12-hour": "settings/n12Hour_12il43",
  "24-hour": "settings/n24Hour_ecflcz",
} as const;

function AboutVersionSection() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const updateStateQuery = useDesktopUpdateState();
  const [isChangingUpdateChannel, setIsChangingUpdateChannel] = useState(false);

  const updateState = updateStateQuery.data ?? null;
  const hasDesktopBridge = typeof window !== "undefined" && Boolean(window.desktopBridge);
  const selectedUpdateChannel = updateState?.channel ?? "latest";

  const handleUpdateChannelChange = useCallback(
    (channel: DesktopUpdateChannel) => {
      const bridge = window.desktopBridge;
      if (
        !bridge ||
        typeof bridge.setUpdateChannel !== "function" ||
        channel === selectedUpdateChannel
      ) {
        return;
      }

      setIsChangingUpdateChannel(true);
      void bridge
        .setUpdateChannel(channel)
        .then((state) => {
          setDesktopUpdateStateQueryData(queryClient, state);
        })
        .catch((error: unknown) => {
          toastManager.add(
            stackedThreadToast({
              type: "error",
              title: t("settings/couldNotChangeUpdate_bm7fka"),
              description:
                error instanceof Error
                  ? error.message
                  : t("settings/updateTrackChangeFailed_6q0lsk"),
            }),
          );
        })
        .finally(() => {
          setIsChangingUpdateChannel(false);
        });
    },
    [queryClient, selectedUpdateChannel, t],
  );

  const handleButtonClick = useCallback(() => {
    const bridge = window.desktopBridge;
    if (!bridge) return;

    const action = updateState ? resolveDesktopUpdateButtonAction(updateState) : "none";

    if (action === "download") {
      void bridge
        .downloadUpdate()
        .then((result) => {
          setDesktopUpdateStateQueryData(queryClient, result.state);
        })
        .catch((error: unknown) => {
          toastManager.add(
            stackedThreadToast({
              type: "error",
              title: t("common/couldNotDownloadUpdate_953u1x"),
              description:
                error instanceof Error ? error.message : t("settings/downloadFailed_x0kp1b"),
            }),
          );
        });
      return;
    }

    if (action === "install") {
      const confirmed = window.confirm(
        getDesktopUpdateInstallConfirmationMessage(
          updateState ?? { availableVersion: null, downloadedVersion: null },
        ),
      );
      if (!confirmed) return;
      void bridge
        .installUpdate()
        .then((result) => {
          setDesktopUpdateStateQueryData(queryClient, result.state);
        })
        .catch((error: unknown) => {
          toastManager.add(
            stackedThreadToast({
              type: "error",
              title: t("common/couldNotInstallUpdate_4qde5k"),
              description:
                error instanceof Error ? error.message : t("settings/installFailed_816mp9"),
            }),
          );
        });
      return;
    }

    if (typeof bridge.checkForUpdate !== "function") return;
    void bridge
      .checkForUpdate()
      .then((result) => {
        setDesktopUpdateStateQueryData(queryClient, result.state);
        if (!result.checked) {
          toastManager.add(
            stackedThreadToast({
              type: "error",
              title: t("settings/couldNotCheckFor_9hx6i8"),
              description: result.state.message ?? t("settings/automaticUpdatesAreNot_nxc79n"),
            }),
          );
        }
      })
      .catch((error: unknown) => {
        toastManager.add(
          stackedThreadToast({
            type: "error",
            title: t("settings/couldNotCheckFor_9hx6i8"),
            description:
              error instanceof Error ? error.message : t("settings/updateCheckFailed_h4wbnh"),
          }),
        );
      });
  }, [queryClient, t, updateState]);

  const action = updateState ? resolveDesktopUpdateButtonAction(updateState) : "none";
  const buttonTooltip = updateState ? getDesktopUpdateButtonTooltip(updateState) : null;
  const buttonDisabled =
    action === "none"
      ? !canCheckForUpdate(updateState)
      : isDesktopUpdateButtonDisabled(updateState);

  const actionLabel: Record<string, string> = {
    download: t("settings/download_1te88l"),
    install: t("settings/install_w4cm6i"),
  };
  const statusLabel: Record<string, string> = {
    checking: t("settings/checking_szdx8x"),
    downloading: t("common/downloading_fop82y"),
    "up-to-date": t("settings/upToDate_a279q4"),
  };
  const buttonLabel =
    actionLabel[action] ??
    statusLabel[updateState?.status ?? ""] ??
    t("settings/checkForUpdates_epkl9u");
  const description =
    action === "download" || action === "install" ? t("settings/updateAvailable_bl03yq") : null;

  return (
    <div className="flex items-center gap-2">
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              size="xs"
              variant={action === "install" ? "default" : "outline"}
              disabled={buttonDisabled}
              onClick={handleButtonClick}
            >
              {buttonLabel}
            </Button>
          }
        />
        {buttonTooltip ? <TooltipPopup>{buttonTooltip}</TooltipPopup> : null}
      </Tooltip>
      {hasDesktopBridge ? (
        <Select
          value={selectedUpdateChannel}
          onValueChange={(value) => {
            handleUpdateChannelChange(value as DesktopUpdateChannel);
          }}
        >
          <SelectTrigger
            size="xs"
            className="w-28 min-w-0"
            aria-label={t("settings/updateTrack_70iy8f")}
            title={t("settings/stableFollowsFullReleases_y2aock")}
            disabled={isChangingUpdateChannel}
          >
            <SelectValue>
              {selectedUpdateChannel === "nightly"
                ? t("settings/nightly_82ks48")
                : t("settings/stable_yjf5zo")}
            </SelectValue>
          </SelectTrigger>
          <SelectPopup align="end" alignItemWithTrigger={false}>
            <SelectItem hideIndicator value="latest">
              {t("settings/stable_yjf5zo")}
            </SelectItem>
            <SelectItem hideIndicator value="nightly">
              {t("settings/nightly_82ks48")}
            </SelectItem>
          </SelectPopup>
        </Select>
      ) : null}
      {description ? <span className="text-xs text-muted-foreground">{description}</span> : null}
    </div>
  );
}

export function useSettingsRestore(onRestored?: () => void) {
  const { theme, setTheme } = useTheme();
  const { t, i18n } = useTranslation();
  const locale = i18n.language as Locale;
  const settings = useSettings();
  const { updateSettings } = useUpdateSettings();

  const changedSettingLabels = useMemo(
    () => [
      ...(theme !== "system" ? [t("settings/theme_ynouwi")] : []),
      ...(locale !== DEFAULT_LOCALE ? [t("settings/language_jnjy32")] : []),
      ...(settings.timestampFormat !== DEFAULT_CLIENT_SETTINGS.timestampFormat
        ? [t("settings/timeFormat_7um2w7")]
        : []),
      ...(settings.sidebarThreadPreviewCount !== DEFAULT_CLIENT_SETTINGS.sidebarThreadPreviewCount
        ? [t("common/visibleThreads_cqgaks")]
        : []),
      ...(settings.autoOpenPlanSidebar !== DEFAULT_CLIENT_SETTINGS.autoOpenPlanSidebar
        ? [t("settings/autoOpenTaskPanel_xg2mzo")]
        : []),

      ...(settings.confirmThreadArchive !== DEFAULT_CLIENT_SETTINGS.confirmThreadArchive
        ? [t("settings/archiveConfirmation_qhf6rf")]
        : []),
      ...(settings.confirmThreadDelete !== DEFAULT_CLIENT_SETTINGS.confirmThreadDelete
        ? [t("settings/deleteConfirmation_epwz3z")]
        : []),
    ],
    [
      locale,
      t,
      settings.autoOpenPlanSidebar,
      settings.confirmThreadArchive,
      settings.confirmThreadDelete,
      settings.sidebarThreadPreviewCount,
      settings.timestampFormat,
      theme,
    ],
  );

  const restoreDefaults = useCallback(async () => {
    if (changedSettingLabels.length === 0) return;
    const api = readLocalApi();
    const changedList = changedSettingLabels.join(", ");
    const confirmed = await (api ?? ensureLocalApi()).dialogs.confirm(
      [
        t("settings/restoreDefaultSettings_vtgxk5"),
        t("settings/thisWillReset_pw4l4o", { changedList }),
      ].join("\n"),
    );
    if (!confirmed) return;

    setTheme("system");
    setLocale(DEFAULT_LOCALE);
    updateSettings({
      timestampFormat: DEFAULT_CLIENT_SETTINGS.timestampFormat,
      sidebarThreadPreviewCount: DEFAULT_CLIENT_SETTINGS.sidebarThreadPreviewCount,
      autoOpenPlanSidebar: DEFAULT_CLIENT_SETTINGS.autoOpenPlanSidebar,
      confirmThreadArchive: DEFAULT_CLIENT_SETTINGS.confirmThreadArchive,
      confirmThreadDelete: DEFAULT_CLIENT_SETTINGS.confirmThreadDelete,
    });
    onRestored?.();
  }, [changedSettingLabels, onRestored, setTheme, t, updateSettings]);

  return {
    changedSettingLabels,
    restoreDefaults,
  };
}

function DiagnosticsSettingsRow() {
  const { t } = useTranslation();
  const observability = useServerObservability();
  const diagnosticsDescription = formatDiagnosticsDescription({
    localTracingEnabled: observability?.localTracingEnabled ?? false,
    otlpTracesEnabled: observability?.otlpTracesEnabled ?? false,
    otlpTracesUrl: observability?.otlpTracesUrl,
    otlpMetricsEnabled: observability?.otlpMetricsEnabled ?? false,
    otlpMetricsUrl: observability?.otlpMetricsUrl,
  });

  return (
    <SettingsRow
      title={t("settings/diagnostics_2vh5a6")}
      description={diagnosticsDescription}
      control={
        <Button render={<Link to="/settings/diagnostics" />} size="xs" variant="outline">
          {t("settings/viewDiagnostics_embk0g")}
        </Button>
      }
    />
  );
}

export function GeneralSettingsPanel() {
  const { theme, setTheme } = useTheme();
  const { t, i18n } = useTranslation();
  const locale = i18n.language as Locale;
  const settings = useSettings();
  const { updateSettings } = useUpdateSettings();
  const versionLabel = useAppVersion();

  return (
    <SettingsPageContainer>
      <SettingsSection title={t("settings/general_pvqyn0")}>
        <SettingsRow
          title={t("settings/theme_ynouwi")}
          description={t("settings/chooseHowLooksAcross_i57qvx", { APP_BASE_NAME })}
          resetAction={
            theme !== "system" ? (
              <SettingResetButton
                label={t("settings/theme_sxxp4v")}
                onClick={() => setTheme("system")}
              />
            ) : null
          }
          control={
            <Select
              value={theme}
              onValueChange={(value) => {
                if (value === "system" || value === "light" || value === "dark") {
                  setTheme(value);
                }
              }}
            >
              <SelectTrigger
                className="w-full sm:w-40"
                aria-label={t("settings/themePreference_j609c1")}
              >
                <SelectValue>
                  {i18n.t(
                    THEME_OPTIONS.find((option) => option.value === theme)?.label ??
                      "settings/system_ip84wd",
                  )}
                </SelectValue>
              </SelectTrigger>
              <SelectPopup align="end" alignItemWithTrigger={false}>
                {THEME_OPTIONS.map((option) => (
                  <SelectItem hideIndicator key={option.value} value={option.value}>
                    {i18n.t(option.label)}
                  </SelectItem>
                ))}
              </SelectPopup>
            </Select>
          }
        />

        <SettingsRow
          title={t("settings/language_jnjy32")}
          description={t("settings/languageOfTheApp_86tsak")}
          resetAction={
            locale !== DEFAULT_LOCALE ? (
              <SettingResetButton
                label={t("settings/language_ffnsmc")}
                onClick={() => setLocale(DEFAULT_LOCALE)}
              />
            ) : null
          }
          control={
            <Select
              value={locale}
              onValueChange={(value) => {
                if (isLocale(value)) {
                  setLocale(value);
                }
              }}
            >
              <SelectTrigger
                className="w-full sm:w-40"
                aria-label={t("settings/languagePreference_6f9lpm")}
              >
                <SelectValue>{LOCALE_LABELS[locale]}</SelectValue>
              </SelectTrigger>
              <SelectPopup align="end" alignItemWithTrigger={false}>
                {LOCALES.map((option) => (
                  <SelectItem hideIndicator key={option} value={option}>
                    {LOCALE_LABELS[option]}
                  </SelectItem>
                ))}
              </SelectPopup>
            </Select>
          }
        />

        <SettingsRow
          title={t("settings/timeFormat_7um2w7")}
          description={t("settings/systemDefaultFollowsYour_eyv071")}
          resetAction={
            settings.timestampFormat !== DEFAULT_CLIENT_SETTINGS.timestampFormat ? (
              <SettingResetButton
                label={t("settings/timeFormat_vswrae")}
                onClick={() =>
                  updateSettings({
                    timestampFormat: DEFAULT_CLIENT_SETTINGS.timestampFormat,
                  })
                }
              />
            ) : null
          }
          control={
            <Select
              value={settings.timestampFormat}
              onValueChange={(value) => {
                if (value === "locale" || value === "12-hour" || value === "24-hour") {
                  updateSettings({ timestampFormat: value });
                }
              }}
            >
              <SelectTrigger
                className="w-full sm:w-40"
                aria-label={t("settings/timestampFormat_7pagsq")}
              >
                <SelectValue>
                  {i18n.t(TIMESTAMP_FORMAT_LABELS[settings.timestampFormat])}
                </SelectValue>
              </SelectTrigger>
              <SelectPopup align="end" alignItemWithTrigger={false}>
                <SelectItem hideIndicator value="locale">
                  {i18n.t(TIMESTAMP_FORMAT_LABELS.locale)}
                </SelectItem>
                <SelectItem hideIndicator value="12-hour">
                  {i18n.t(TIMESTAMP_FORMAT_LABELS["12-hour"])}
                </SelectItem>
                <SelectItem hideIndicator value="24-hour">
                  {i18n.t(TIMESTAMP_FORMAT_LABELS["24-hour"])}
                </SelectItem>
              </SelectPopup>
            </Select>
          }
        />

        <SettingsRow
          title={t("settings/autoOpenTaskPanel_xg2mzo")}
          description={t("settings/openTheRightSide_iuh2fc")}
          resetAction={
            settings.autoOpenPlanSidebar !== DEFAULT_CLIENT_SETTINGS.autoOpenPlanSidebar ? (
              <SettingResetButton
                label={t("settings/autoOpenTaskPanel_7btrc3")}
                onClick={() =>
                  updateSettings({
                    autoOpenPlanSidebar: DEFAULT_CLIENT_SETTINGS.autoOpenPlanSidebar,
                  })
                }
              />
            ) : null
          }
          control={
            <Switch
              checked={settings.autoOpenPlanSidebar}
              onCheckedChange={(checked) =>
                updateSettings({ autoOpenPlanSidebar: Boolean(checked) })
              }
              aria-label={t("settings/openTheTaskPanel_uupaf1")}
            />
          }
        />

        <SettingsRow
          title={t("settings/archiveConfirmation_qhf6rf")}
          description={t("settings/requireASecondClick_5eie0m")}
          resetAction={
            settings.confirmThreadArchive !== DEFAULT_CLIENT_SETTINGS.confirmThreadArchive ? (
              <SettingResetButton
                label={t("settings/archiveConfirmation_o3nake")}
                onClick={() =>
                  updateSettings({
                    confirmThreadArchive: DEFAULT_CLIENT_SETTINGS.confirmThreadArchive,
                  })
                }
              />
            ) : null
          }
          control={
            <Switch
              checked={settings.confirmThreadArchive}
              onCheckedChange={(checked) =>
                updateSettings({ confirmThreadArchive: Boolean(checked) })
              }
              aria-label={t("settings/confirmThreadArchiving_iapb8s")}
            />
          }
        />

        <SettingsRow
          title={t("settings/deleteConfirmation_epwz3z")}
          description={t("settings/askBeforeDeletingA_j8heas")}
          resetAction={
            settings.confirmThreadDelete !== DEFAULT_CLIENT_SETTINGS.confirmThreadDelete ? (
              <SettingResetButton
                label={t("settings/deleteConfirmation_0wag4s")}
                onClick={() =>
                  updateSettings({
                    confirmThreadDelete: DEFAULT_CLIENT_SETTINGS.confirmThreadDelete,
                  })
                }
              />
            ) : null
          }
          control={
            <Switch
              checked={settings.confirmThreadDelete}
              onCheckedChange={(checked) =>
                updateSettings({ confirmThreadDelete: Boolean(checked) })
              }
              aria-label={t("settings/confirmThreadDeletion_nd593v")}
            />
          }
        />
        {import.meta.env.DEV && <DiagnosticsSettingsRow />}
      </SettingsSection>
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-1">
        <p className="text-xs text-muted-foreground">
          <Trans
            t={t}
            i18nKey={isElectron ? "settings/version_cc72me" : "settings/message_3vf3n4"}
            values={{
              product: isElectron ? APP_BASE_NAME : t("settings/dovodWorkspace_wqsrkf"),
              version: APP_VERSION,
              versionLabel,
            }}
            components={[<span key="product" className="font-semibold text-foreground" />]}
          />
          <span aria-hidden="true" className="mx-1">
            ·
          </span>
          <a
            href="https://buro1.ai"
            target="_blank"
            rel="noopener noreferrer"
            className="text-foreground underline decoration-foreground/30 underline-offset-[3px] hover:decoration-foreground"
          >
            {t("settings/buro1_82nfwx")}
          </a>
        </p>
        {isElectron && <AboutVersionSection />}
      </div>
    </SettingsPageContainer>
  );
}

export function ArchivedThreadsPanel() {
  const { t } = useTranslation();
  const primaryEnvironmentId = usePrimaryEnvironmentId();
  const projects = useStore(useShallow(selectProjectsAcrossEnvironments));
  const { unarchiveThread, confirmAndDeleteThread } = useThreadActions();
  const environmentIds = useMemo(
    () => [
      ...new Set([
        ...(primaryEnvironmentId === null ? [] : [primaryEnvironmentId]),
        ...projects.map((project) => project.environmentId),
      ]),
    ],
    [projects, primaryEnvironmentId],
  );
  const {
    snapshots: archivedSnapshots,
    error: archiveError,
    isLoading: isLoadingArchive,
    refresh: refreshArchivedThreads,
  } = useArchivedThreadSnapshots(environmentIds);

  const archivedGroups = useMemo(() => {
    const projectsByEnvironmentAndId = new Map(
      archivedSnapshots.flatMap(({ environmentId, snapshot }) =>
        snapshot.projects.map(
          (project) =>
            [
              `${environmentId}:${project.id}`,
              {
                id: project.id,
                environmentId,
                name: project.title,
                cwd: project.workspaceRoot,
                createdBy: project.createdBy,
                archivedAt: project.archivedAt ?? null,
              },
            ] as const,
        ),
      ),
    );
    const threads = archivedSnapshots.flatMap(({ environmentId, snapshot }) =>
      snapshot.threads.map((thread) => ({
        ...thread,
        environmentId,
      })),
    );

    const archivedProjects = Array.from(projectsByEnvironmentAndId.values());
    const groups: Array<{
      readonly project: (typeof archivedProjects)[number];
      readonly threads: Array<(typeof threads)[number]>;
    }> = [];
    for (const project of archivedProjects) {
      const projectThreads: Array<(typeof threads)[number]> = [];
      for (const thread of threads) {
        if (thread.projectId === project.id && thread.environmentId === project.environmentId) {
          projectThreads.push(thread);
        }
      }
      if (project.archivedAt !== null || projectThreads.length > 0) {
        groups.push({
          project,
          threads: projectThreads.toSorted((left, right) => {
            const leftKey = left.archivedAt ?? left.createdAt;
            const rightKey = right.archivedAt ?? right.createdAt;
            return rightKey.localeCompare(leftKey) || right.id.localeCompare(left.id);
          }),
        });
      }
    }
    return groups;
  }, [archivedSnapshots]);

  const handleArchivedThreadContextMenu = useCallback(
    async (threadRef: ScopedThreadRef, position: { x: number; y: number }) => {
      const api = readLocalApi();
      if (!api) return;
      const clicked = await api.contextMenu.show(
        [
          { id: "unarchive", label: t("settings/unarchive_6ne1aq") },
          { id: "delete", label: t("common/delete_ivk488"), destructive: true },
        ],
        position,
      );

      if (clicked === "unarchive") {
        try {
          await unarchiveThread(threadRef);
          refreshArchivedThreads();
        } catch (error) {
          toastManager.add(
            stackedThreadToast({
              type: "error",
              title: t("settings/failedToUnarchiveThread_9iwg8m"),
              description:
                error instanceof Error ? error.message : t("common/anErrorOccurred_gd0s0s"),
            }),
          );
        }
        return;
      }

      if (clicked === "delete") {
        await confirmAndDeleteThread(threadRef);
        refreshArchivedThreads();
      }
    },
    [confirmAndDeleteThread, refreshArchivedThreads, t, unarchiveThread],
  );

  return (
    <SettingsPageContainer>
      {archivedGroups.length === 0 ? (
        <SettingsSection title={t("settings/archived_c05kag")}>
          <SettingsRow
            title={
              <span className="inline-flex items-center gap-2">
                {isLoadingArchive ? (
                  <LoaderIcon className="size-3.5 animate-spin text-muted-foreground" />
                ) : (
                  <ArchiveIcon className="size-3.5 text-muted-foreground" />
                )}
                {isLoadingArchive
                  ? t("settings/loadingArchivedThreads_b78lfg")
                  : archiveError
                    ? t("settings/couldNotLoadArchived_jkpeg0")
                    : t("settings/noArchivedThreads_yg0nxi")}
              </span>
            }
            description={
              isLoadingArchive
                ? t("settings/checkingConnectedEnvironments_6k90sk")
                : (archiveError ?? t("settings/archivedThreadsWillAppear_99zt1x"))
            }
          />
        </SettingsSection>
      ) : (
        archivedGroups.map(({ project, threads: projectThreads }) => (
          <SettingsSection
            key={project.id}
            title={project.name}
            icon={<ProjectFavicon environmentId={project.environmentId} cwd={project.cwd} />}
          >
            {project.archivedAt !== null && (
              <SettingsRow
                title={project.name}
                description={null}
                control={
                  <ArchivedProjectActions project={project} onChanged={refreshArchivedThreads} />
                }
              />
            )}
            {projectThreads.map((thread) => (
              <SettingsRow
                key={thread.id}
                onContextMenu={(event) => {
                  event.preventDefault();
                  if (project.archivedAt !== null) return;
                  void handleArchivedThreadContextMenu(
                    scopeThreadRef(thread.environmentId, thread.id),
                    {
                      x: event.clientX,
                      y: event.clientY,
                    },
                  );
                }}
                title={thread.title}
                description={t("settings/archivedCreated_zy6wj5", {
                  "0": formatRelativeTimeLabel(
                    thread.archivedAt ?? project.archivedAt ?? thread.createdAt,
                  ),
                  "1": formatRelativeTimeLabel(thread.createdAt),
                })}
                control={
                  project.archivedAt !== null ? undefined : (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 shrink-0 cursor-pointer gap-1.5 px-2.5"
                      onClick={() =>
                        void unarchiveThread(scopeThreadRef(thread.environmentId, thread.id))
                          .then(() => refreshArchivedThreads())
                          .catch((error) => {
                            toastManager.add(
                              stackedThreadToast({
                                type: "error",
                                title: t("settings/failedToUnarchiveThread_9iwg8m"),
                                description:
                                  error instanceof Error
                                    ? error.message
                                    : t("common/anErrorOccurred_gd0s0s"),
                              }),
                            );
                          })
                      }
                    >
                      <ArchiveX className="size-3.5" />
                      <span>{t("settings/unarchive_6ne1aq")}</span>
                    </Button>
                  )
                }
              />
            ))}
          </SettingsSection>
        ))
      )}
    </SettingsPageContainer>
  );
}
